import React, { useState, useEffect } from 'react';
import './BrandingSetup.css';

const BrandingSetup = ({ organizerId, initialBranding }) => {
  const defaultBranding = {
    logo_base64: '',
    primary_color: '#3B82F6',
    secondary_color: '#1E40AF',
    font_family: 'Inter, sans-serif',
    // Background colors
    page_background: '#f5f7fa',
    panel_background: '#ffffff',
    header_background: '#ffffff',
    // Text colors
    primary_text_color: '#1a1a1a',
    secondary_text_color: '#666666',
    heading_color: '#1a1a1a',
    // Button styles
    button_background: '#3B82F6',
    button_text_color: '#ffffff',
    button_border_radius: '6px',
    button_hover_opacity: '0.9',
    // Chat bubble colors
    ai_bubble_background: '#eef6ff',
    ai_bubble_text_color: '#062b4a',
    user_bubble_background: '#25d366',
    user_bubble_text_color: '#ffffff',
    // Border radius
    panel_border_radius: '8px',
    bubble_border_radius: '18px',
    // Spacing
    panel_padding: '20px',
    bubble_padding: '12px 16px',
  };

  const [branding, setBranding] = useState(() => {
    // Merge initial branding with defaults
    if (initialBranding) {
      return { ...defaultBranding, ...initialBranding };
    }
    return defaultBranding;
  });
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    // If initial branding is provided, use it instead of loading from server
    if (initialBranding) {
      setBranding(prev => ({ ...prev, ...initialBranding }));
      setMessage('Branding extracted from your website! Review and customize as needed.');
      setTimeout(() => setMessage(''), 5000);
    } else {
      loadBranding();
    }
  }, [organizerId, initialBranding]);

  const loadBranding = async () => {
    try {
      const response = await fetch(`/api/organizer/${organizerId}/branding`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setBranding({
          logo_base64: data.logo_base64 || '',
          primary_color: data.primary_color || '#3B82F6',
          secondary_color: data.secondary_color || '#1E40AF',
          font_family: data.font_family || 'Inter, sans-serif',
          page_background: data.page_background || '#f5f7fa',
          panel_background: data.panel_background || '#ffffff',
          header_background: data.header_background || '#ffffff',
          primary_text_color: data.primary_text_color || '#1a1a1a',
          secondary_text_color: data.secondary_text_color || '#666666',
          heading_color: data.heading_color || '#1a1a1a',
          button_background: data.button_background || '#3B82F6',
          button_text_color: data.button_text_color || '#ffffff',
          button_border_radius: data.button_border_radius || '6px',
          button_hover_opacity: data.button_hover_opacity || '0.9',
          ai_bubble_background: data.ai_bubble_background || '#eef6ff',
          ai_bubble_text_color: data.ai_bubble_text_color || '#062b4a',
          user_bubble_background: data.user_bubble_background || '#25d366',
          user_bubble_text_color: data.user_bubble_text_color || '#ffffff',
          panel_border_radius: data.panel_border_radius || '8px',
          bubble_border_radius: data.bubble_border_radius || '18px',
          panel_padding: data.panel_padding || '20px',
          bubble_padding: data.bubble_padding || '12px 16px',
        });
      }
    } catch (err) {
      console.error('Failed to load branding:', err);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBranding({
          ...branding,
          logo_base64: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // First attempt to save
      let response = await fetch(`/api/organizer/${organizerId}/branding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(branding),
      });

      // If error about missing columns, try to run migration and retry
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorText = errorData.error || '';
        
        // Check if it's a column error
        if (errorText.includes('Unknown column') || errorText.includes('column')) {
          setMessage('Running database migration...');
          
          // Trigger migration
          const migrationResponse = await fetch('/api/migrate/branding-columns', {
            method: 'POST',
            credentials: 'include',
          });
          
          if (migrationResponse.ok) {
            // Retry saving after migration
            setMessage('Migration successful. Saving branding...');
            response = await fetch(`/api/organizer/${organizerId}/branding`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              credentials: 'include',
              body: JSON.stringify(branding),
            });
          } else {
            const migrationError = await migrationResponse.json().catch(() => ({}));
            setMessage(migrationError.error || 'Migration failed. Please contact support.');
            setLoading(false);
            return;
          }
        }
      }

      if (response.ok) {
        setMessage('Branding updated successfully!');
        localStorage.setItem('organizer_branding', JSON.stringify(branding));
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json().catch(() => ({}));
        setMessage(data.error || 'Failed to update branding');
      }
    } catch (err) {
      console.error('Save error:', err);
      setMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateBranding = (key, value) => {
    setBranding({ ...branding, [key]: value });
  };

  return (
    <div className="branding-setup">
      <h2>Branding Setup</h2>
      <p className="subtitle">Customize how your screening experience looks to participants</p>

      {message && (
        <div className={message.includes('success') ? 'success-message' : 'error-message'}>
          {message}
        </div>
      )}

      <div className="branding-container">
        <div className="branding-controls">
          <div className="tabs">
            <button
              type="button"
              className={activeTab === 'basic' ? 'active' : ''}
              onClick={() => setActiveTab('basic')}
            >
              Basic
            </button>
            <button
              type="button"
              className={activeTab === 'colors' ? 'active' : ''}
              onClick={() => setActiveTab('colors')}
            >
              Colors
            </button>
            <button
              type="button"
              className={activeTab === 'buttons' ? 'active' : ''}
              onClick={() => setActiveTab('buttons')}
            >
              Buttons
            </button>
            <button
              type="button"
              className={activeTab === 'chat' ? 'active' : ''}
              onClick={() => setActiveTab('chat')}
            >
              Chat
            </button>
            <button
              type="button"
              className={activeTab === 'advanced' ? 'active' : ''}
              onClick={() => setActiveTab('advanced')}
            >
              Advanced
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {activeTab === 'basic' && (
              <>
                <div className="form-section">
                  <label>Company Logo</label>
                  <div className="logo-upload">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="file-input"
                    />
                    {branding.logo_base64 && (
                      <div className="logo-preview">
                        <img src={branding.logo_base64} alt="Logo preview" />
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-section">
                  <label>Font Family</label>
                  <select
                    value={branding.font_family}
                    onChange={(e) => updateBranding('font_family', e.target.value)}
                  >
                    <option value="Inter, sans-serif">Inter</option>
                    <option value="Roboto, sans-serif">Roboto</option>
                    <option value="Open Sans, sans-serif">Open Sans</option>
                    <option value="Lato, sans-serif">Lato</option>
                    <option value="Montserrat, sans-serif">Montserrat</option>
                    <option value="Poppins, sans-serif">Poppins</option>
                    <option value="Nunito, sans-serif">Nunito</option>
                    <option value="Raleway, sans-serif">Raleway</option>
                  </select>
                </div>

                <div className="form-section">
                  <label>Primary Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.primary_color}
                      onChange={(e) => updateBranding('primary_color', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.primary_color}
                      onChange={(e) => updateBranding('primary_color', e.target.value)}
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>Secondary Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.secondary_color}
                      onChange={(e) => updateBranding('secondary_color', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.secondary_color}
                      onChange={(e) => updateBranding('secondary_color', e.target.value)}
                      placeholder="#1E40AF"
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'colors' && (
              <>
                <div className="form-section">
                  <label>Page Background</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.page_background}
                      onChange={(e) => updateBranding('page_background', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.page_background}
                      onChange={(e) => updateBranding('page_background', e.target.value)}
                      placeholder="#f5f7fa"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>Panel Background</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.panel_background}
                      onChange={(e) => updateBranding('panel_background', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.panel_background}
                      onChange={(e) => updateBranding('panel_background', e.target.value)}
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>Header Background</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.header_background}
                      onChange={(e) => updateBranding('header_background', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.header_background}
                      onChange={(e) => updateBranding('header_background', e.target.value)}
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>Primary Text Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.primary_text_color}
                      onChange={(e) => updateBranding('primary_text_color', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.primary_text_color}
                      onChange={(e) => updateBranding('primary_text_color', e.target.value)}
                      placeholder="#1a1a1a"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>Secondary Text Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.secondary_text_color}
                      onChange={(e) => updateBranding('secondary_text_color', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.secondary_text_color}
                      onChange={(e) => updateBranding('secondary_text_color', e.target.value)}
                      placeholder="#666666"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>Heading Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.heading_color}
                      onChange={(e) => updateBranding('heading_color', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.heading_color}
                      onChange={(e) => updateBranding('heading_color', e.target.value)}
                      placeholder="#1a1a1a"
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'buttons' && (
              <>
                <div className="form-section">
                  <label>Button Background</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.button_background}
                      onChange={(e) => updateBranding('button_background', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.button_background}
                      onChange={(e) => updateBranding('button_background', e.target.value)}
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>Button Text Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.button_text_color}
                      onChange={(e) => updateBranding('button_text_color', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.button_text_color}
                      onChange={(e) => updateBranding('button_text_color', e.target.value)}
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>Button Border Radius (px)</label>
                  <input
                    type="number"
                    value={parseInt(branding.button_border_radius)}
                    onChange={(e) => updateBranding('button_border_radius', `${e.target.value}px`)}
                    min="0"
                    max="50"
                  />
                </div>

                <div className="form-section">
                  <label>Button Hover Opacity</label>
                  <input
                    type="range"
                    min="0.5"
                    max="1"
                    step="0.05"
                    value={branding.button_hover_opacity}
                    onChange={(e) => updateBranding('button_hover_opacity', e.target.value)}
                  />
                  <span className="range-value">{branding.button_hover_opacity}</span>
                </div>
              </>
            )}

            {activeTab === 'chat' && (
              <>
                <div className="form-section">
                  <label>AI Bubble Background</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.ai_bubble_background}
                      onChange={(e) => updateBranding('ai_bubble_background', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.ai_bubble_background}
                      onChange={(e) => updateBranding('ai_bubble_background', e.target.value)}
                      placeholder="#eef6ff"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>AI Bubble Text Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.ai_bubble_text_color}
                      onChange={(e) => updateBranding('ai_bubble_text_color', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.ai_bubble_text_color}
                      onChange={(e) => updateBranding('ai_bubble_text_color', e.target.value)}
                      placeholder="#062b4a"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>User Bubble Background</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.user_bubble_background}
                      onChange={(e) => updateBranding('user_bubble_background', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.user_bubble_background}
                      onChange={(e) => updateBranding('user_bubble_background', e.target.value)}
                      placeholder="#25d366"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>User Bubble Text Color</label>
                  <div className="color-input-group">
                    <input
                      type="color"
                      value={branding.user_bubble_text_color}
                      onChange={(e) => updateBranding('user_bubble_text_color', e.target.value)}
                    />
                    <input
                      type="text"
                      value={branding.user_bubble_text_color}
                      onChange={(e) => updateBranding('user_bubble_text_color', e.target.value)}
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                <div className="form-section">
                  <label>Bubble Border Radius (px)</label>
                  <input
                    type="number"
                    value={parseInt(branding.bubble_border_radius)}
                    onChange={(e) => updateBranding('bubble_border_radius', `${e.target.value}px`)}
                    min="0"
                    max="50"
                  />
                </div>
              </>
            )}

            {activeTab === 'advanced' && (
              <>
                <div className="form-section">
                  <label>Panel Border Radius (px)</label>
                  <input
                    type="number"
                    value={parseInt(branding.panel_border_radius)}
                    onChange={(e) => updateBranding('panel_border_radius', `${e.target.value}px`)}
                    min="0"
                    max="50"
                  />
                </div>

                <div className="form-section">
                  <label>Panel Padding (px)</label>
                  <input
                    type="number"
                    value={parseInt(branding.panel_padding)}
                    onChange={(e) => updateBranding('panel_padding', `${e.target.value}px`)}
                    min="0"
                    max="50"
                  />
                </div>

                <div className="form-section">
                  <label>Bubble Padding</label>
                  <input
                    type="text"
                    value={branding.bubble_padding}
                    onChange={(e) => updateBranding('bubble_padding', e.target.value)}
                    placeholder="12px 16px"
                  />
                  <small>Format: "vertical horizontal" (e.g., "12px 16px")</small>
                </div>
              </>
            )}

            <button type="submit" disabled={loading} className="save-btn">
              {loading ? 'Saving...' : 'Save Branding'}
            </button>
          </form>
        </div>

        <div className="preview-section1">
          <h3>Live Preview</h3>
          <p className="preview-subtitle">This is how participants will see your screening test</p>
          <div
            className="screening-preview"
            style={{
              '--primary-color': branding.primary_color,
              '--secondary-color': branding.secondary_color,
              '--page-bg': branding.page_background,
              '--panel-bg': branding.panel_background,
              '--header-bg': branding.header_background,
              '--primary-text': branding.primary_text_color,
              '--secondary-text': branding.secondary_text_color,
              '--heading-color': branding.heading_color,
              '--button-bg': branding.button_background,
              '--button-text': branding.button_text_color,
              '--button-radius': branding.button_border_radius,
              '--button-hover': branding.button_hover_opacity,
              '--ai-bubble-bg': branding.ai_bubble_background,
              '--ai-bubble-text': branding.ai_bubble_text_color,
              '--user-bubble-bg': branding.user_bubble_background,
              '--user-bubble-text': branding.user_bubble_text_color,
              '--panel-radius': branding.panel_border_radius,
              '--bubble-radius': branding.bubble_border_radius,
              '--panel-padding': branding.panel_padding,
              fontFamily: branding.font_family,
            }}
          >
            <div className="preview-header">
              {branding.logo_base64 && (
                <img src={branding.logo_base64} alt="Logo" className="preview-header-logo" />
              )}
              <div className="preview-status-indicators">
                <div className="preview-status-dot connected"></div>
                <span>Connected</span>
                <span className="preview-listening-indicator">Listening...</span>
              </div>
            </div>

            <div className="preview-content">
              <div className="preview-conversation-panel">
                <div className="preview-chat-header">
                  <h3 className="preview-chat-title">Conversation</h3>
                  <div className="preview-listening-badge">
                    <span className="preview-pulse-dot"></span>
                    Listening
                  </div>
                </div>

                <div className="preview-chat-body">
                  <div className="preview-message assistant">
                    <div className="preview-meta">AI</div>
                    <div className="preview-bubble ai-bubble">
                      <div className="preview-bubble-text">
                        Welcome! Let's start with a quick introduction. Can you tell me about your background in software development?
                      </div>
                    </div>
                  </div>

                  <div className="preview-message user">
                    <div className="preview-bubble user-bubble">
                      <div className="preview-bubble-text">
                        I have 5 years of experience working with React and Node.js, focusing on building scalable web applications.
                      </div>
                    </div>
                    <div className="preview-right-meta">You</div>
                  </div>

                  <div className="preview-message assistant">
                    <div className="preview-meta">AI</div>
                    <div className="preview-bubble ai-bubble">
                      <div className="preview-bubble-text">
                        That's great! Can you walk me through how you would handle state management in a large React application?
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="preview-controls-panel">
                <div className="preview-question-box">
                  <h4>Current Question:</h4>
                  <p>Can you walk me through how you would handle state management in a large React application?</p>
                </div>

                <button className="preview-submit-btn">Submit</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandingSetup;

